<template>
  <div>
    <div class="mb-1 d-flex align-baseline">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.users") }}:</div>
      <div>
        <span class="body2">
          <span data-testid="ticket-preview-users">
            <span class="body2 mr-1" data-testid="ticket-preview-user">{{ users[0] }}</span>
            <span
              v-if="users.length > 1"
              role="button"
              data-testid="ticket-preview-plus-n-btn"
              class="plus-one-text"
              @click="openListModal(TicketsModal.USERS, users)"
              >&nbsp;+{{ users.length - 1 }}</span
            >
          </span>
        </span>
      </div>
    </div>

    <users-protection-alert
      v-if="showAddToProtectionBtn"
      class="mb-1"
      :ticket-details="ticketDetails"
      :add-users-not-allowed="addUsersNotAllowed"
      @on-users-add-clicked="addUsers(usersToAdd)"
    />

    <div class="mb-1">
      <span class="subtitle2">{{ $t("ticketDetails.when") }}: </span>
      <span class="body2" data-testid="ticket-preview-when">
        {{ getFormattedDateTime(ticketDetails.endTime, "MMM, DD YYYY, h:mm A") }}
      </span>
    </div>

    <div class="d-flex align-center mb-1">
      <div class="subtitle2">{{ $t("general.service") }}:</div>
      <div class="d-flex align-center">
        <v-icon
          v-if="ticketDetails.sections.serviceName === Service.PROXY"
          size="25"
          icon="$accessRestrictions"
        />
        <coro-icon v-else class="service-icon" :icon-name="ticketDetails.sections.serviceName" />
        <span
          class="body2"
          :data-testid="`ticket-preview-service-${ticketDetails.sections.serviceName}`"
        >
          {{ serviceNameTitle }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { useTicketPreviewGeneralInfo } from "@/composables/useTicketPreviewGeneralInfo";
import intersection from "lodash/intersection";
import { arrayToLowerCase } from "@coronet/cui";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps";
import { TicketsModal, ViolationDirection } from "@/constants/tickets";
import CoroIcon from "@/components/CoroIcon.vue";
import union from "lodash/union";
import UsersProtectionAlert from "@/components/tickets/ticket-preview-details/UsersProtectionAlert.vue";

export default defineComponent({
  components: { UsersProtectionAlert, CoroIcon },
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const { getFormattedDateTime, addUsersNotAllowed, usersToAdd, addUsers, openListModal } =
      useTicketPreviewGeneralInfo(props.ticketDetails);

    const users = computed<string[]>(() => {
      const violationDirection =
        props.ticketDetails?.sections?.userDataAccessViolation?.violationDirection;
      if (violationDirection === ViolationDirection.ACCESS) {
        return union(
          props.ticketDetails.sections.userDataAccessViolation.users,
          props.ticketDetails.potentiallyProtectableUsers
        );
      } else if (violationDirection === ViolationDirection.SHARE) {
        return props.ticketDetails.sections.userDataAccessViolation.users;
      }
      return [props.ticketDetails.sections.triggeredUser];
    });

    const showAddToProtectionBtn = computed(() => {
      // if processed and not white ticket, then the action button will be visible (`Re-open ticket`)
      const showActionBtn = props.ticketDetails.actions?.length;
      const isUserUnprotected = intersection(
        arrayToLowerCase(users.value),
        arrayToLowerCase(props.ticketDetails.potentiallyProtectableUsers)
      ).length;
      return showActionBtn && isUserUnprotected;
    });

    const serviceNameTitle = computed(() =>
      i18n.t(`services.${props.ticketDetails.sections.serviceName}`)
    );

    return {
      getFormattedDateTime,
      addUsersNotAllowed,
      usersToAdd,
      addUsers,
      showAddToProtectionBtn,
      serviceNameTitle,
      openListModal,
      TicketsModal,
      Service,
      users,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  width: 25px;
  height: 25px;
}
</style>
